/**
 * 歌词加载与解析（抽离自 MusicPlayer.js）
 */
import { parseLRC, parseSRT } from './lyricsParsers';
import { cacheLyrics } from '../../utils/musicCache';

/**
 * 根据响应头和内容推断歌词格式
 * 流式歌词可能返回的实际格式与 DB 中的 lyrics_type 不一致
 */
export function inferLyricsType(response, urlToUse, content, lyricsType, lyricsFile) {
  try {
    const isUnifiedStream = typeof urlToUse === 'string' && urlToUse.includes('/api/music/') && urlToUse.includes('/stream') && urlToUse.includes('asset=lyrics');
    if (isUnifiedStream) {
      const ext = (response?.headers?.['x-lyrics-ext'] || response?.headers?.['X-Lyrics-Ext'] || '').toString().trim().toLowerCase();
      if (ext === '.lrc') return 'lrc';
      if (ext === '.srt') return 'srt';
      if (ext === '.txt') return 'txt';
    }

    if (typeof content === 'string') {
      if (/\[\d{1,2}:\d{2}(?:\.\d{2})?\]/.test(content)) return 'lrc';
      if (/\d{1,2}:\d{2}:\d{2},\d{3}\s*-->\s*\d{1,2}:\d{2}:\d{2},\d{3}/.test(content)) return 'srt';
    }

    if (lyricsType === 'lrc' || (lyricsFile && lyricsFile.endsWith('.lrc'))) return 'lrc';
    if (lyricsType === 'srt' || (lyricsFile && lyricsFile.endsWith('.srt'))) return 'srt';
    return 'txt';
  } catch (_) {
    return 'txt';
  }
}

/**
 * 加载歌词（含翻译）
 * @param {object} options
 */
export async function loadLyricsWithTranslation({
  lyricsFile,
  lyricsType,
  music,
  urlToUse,
  axios,
  getLyricsStreamUrl,
  lyricSettings,
  resolveTargetTranslationLang,
  setLyrics,
  lyricsRef,
  clearLyricHighlight,
  updateMiniLyric,
  currentLyricIndexRef,
  currentMusicId
}) {
  const response = await axios.get(urlToUse, { responseType: 'text' });
  const content = response.data;
  const effectiveType = inferLyricsType(response, urlToUse, content, lyricsType, lyricsFile);

  if (urlToUse && effectiveType !== 'txt') {
    cacheLyrics(urlToUse, content, effectiveType).catch(err => {
      console.warn('Failed to cache lyrics:', err);
    });
  }

  let parsedLyrics = [];
  if (effectiveType === 'lrc') {
    parsedLyrics = parseLRC(content);
  } else if (effectiveType === 'srt') {
    parsedLyrics = parseSRT(content);
  } else if (effectiveType === 'txt') {
    console.warn('[Lyrics] TXT lyrics are not supported in player. Skipping display.', {
      musicId: currentMusicId,
      lyricsFile,
      urlToUse
    });
  } else {
    console.warn('Unknown lyrics type, not parsing:', lyricsType);
  }

  if (lyricSettings.showTranslation && music) {
    try {
      const targetLang = resolveTargetTranslationLang(music);
      const translationResponse = await axios.get(`/api/music/${music.id}/lyrics/translation`, {
        params: { lang: targetLang }
      });
      if (translationResponse.data.success && translationResponse.data.data.translation) {
        const translationContent = translationResponse.data.data.translation;
        const translationFormat = translationResponse.data.data.format;

        let translations = [];
        if (translationFormat === 'lrc') {
          translations = parseLRC(translationContent);
        } else if (translationFormat === 'srt') {
          translations = parseSRT(translationContent);
        }

        if (music && translations.length > 0) {
          cacheLyrics(`${lyricsFile}_translation_${targetLang}`, JSON.stringify(translations), lyricsType).catch(err => {
            console.warn('Failed to cache translation lyrics:', err);
          });
        }

        parsedLyrics = parsedLyrics.map((lyric) => {
          const translation = translations.find(t => Math.abs(t.time - lyric.time) < 0.5);
          return { ...lyric, translation: translation ? translation.text : null };
        });
      }
    } catch (error) {
      console.warn('Load translation error:', error);
    }
  }

  setLyrics(parsedLyrics);
  lyricsRef.current = parsedLyrics;
  clearLyricHighlight();
  updateMiniLyric(-1);
  currentLyricIndexRef.current = -1;
  return parsedLyrics;
}
